import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const PreviewContext = createContext(null);

// Map admin section keys → preview iframe target.
// Each entry: { path: string (e.g. "/" or "/philosophy"), anchor?: string (e.g. "#programs") }
const SECTION_TARGETS = {
  // Homepage sections — preview opens / and scrolls to anchor
  hero: { path: "/", anchor: "#home" },
  stats: { path: "/", anchor: "#home" },
  philosophy: { path: "/", anchor: "#philosophy" },
  coach: { path: "/", anchor: "#coach" },
  book: { path: "/", anchor: "#book" },
  ticker: { path: "/", anchor: "#ticker" },
  newsletter: { path: "/", anchor: "#newsletter" },
  footer: { path: "/", anchor: "#contact" },
  logo_strip: { path: "/", anchor: "" },
  programs_header: { path: "/", anchor: "#programs" },
  // Dedicated pages
  page_about: { path: "/about" },
  page_philosophy: { path: "/philosophy" },
  page_programs: { path: "/programs" },
  page_coach: { path: "/coach" },
  page_book: { path: "/book" },
  page_contact: { path: "/contact" },
  page_privacy: { path: "/privacy" },
  page_terms: { path: "/terms" },
  page_cookies: { path: "/cookies" },
};

const ROUTE_TARGETS = [
  { test: /^\/admin\/programs/, target: { path: "/programs" } },
  { test: /^\/admin\/settings/, target: { path: "/" } },
  { test: /^\/admin\/posts/, target: { path: "/" } },
  { test: /^\/admin$/, target: { path: "/" } },
];

const PATH_LABELS = {
  "/": "Homepage",
  "/philosophy": "Philosophy",
  "/programs": "Programs",
  "/about": "About / What is BDH",
  "/coach": "Coach Mike",
  "/book": "The Book",
  "/contact": "Contact",
  "/privacy": "Privacy Policy",
  "/terms": "Terms of Use",
  "/cookies": "Cookie Policy",
};

const ANCHOR_LABELS = {
  "#home": "Hero",
  "#philosophy": "Philosophy",
  "#coach": "Coach Mike",
  "#programs": "Programs",
  "#book": "The Book",
  "#newsletter": "Newsletter",
  "#contact": "Footer",
  "#ticker": "Ticker",
};

export const labelForTarget = (target) => {
  if (!target) return "Live preview";
  const p = PATH_LABELS[target.path] || target.path;
  const a = target.anchor ? ANCHOR_LABELS[target.anchor] : null;
  return a ? `${p} · ${a}` : p;
};

export const PreviewProvider = ({ children }) => {
  // Default open on lg+ screens for first-time admin experience; collapsed on small.
  const [open, setOpen] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem("bdh_preview_open");
      if (stored !== null) return stored === "1";
      return window.matchMedia("(min-width: 1280px)").matches;
    }
    return false;
  });
  const [device, setDevice] = useState("desktop");
  const [target, setTarget] = useState({ path: "/", anchor: "" });
  const [reloadKey, setReloadKey] = useState(0);
  const location = useLocation();

  // Auto-sync target with current admin route
  useEffect(() => {
    const m = location.pathname.match(/^\/admin\/content\/([^/]+)/);
    if (m) {
      setTarget(SECTION_TARGETS[m[1]] || { path: "/" });
      return;
    }
    const hit = ROUTE_TARGETS.find((r) => r.test.test(location.pathname));
    setTarget(hit ? hit.target : { path: "/" });
  }, [location.pathname]);

  const toggle = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("bdh_preview_open", next ? "1" : "0");
      }
      return next;
    });
  }, []);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return (
    <PreviewContext.Provider
      value={{
        open, toggle, setOpen,
        device, setDevice,
        target, setTarget,
        reloadKey, reload,
      }}
    >
      {children}
    </PreviewContext.Provider>
  );
};

export const usePreview = () => {
  const ctx = useContext(PreviewContext);
  if (!ctx) {
    return {
      open: false, toggle: () => {}, setOpen: () => {},
      device: "desktop", setDevice: () => {},
      target: { path: "/" }, setTarget: () => {},
      reloadKey: 0, reload: () => {},
    };
  }
  return ctx;
};
